/* =========================================================================
   La fam — theme behaviour
   Ported from the Astro components. The one substantive change is the cart:
   the static build kept lines in localStorage, this talks to Shopify's Cart
   AJAX API so pricing, discounts and inventory stay authoritative.
   ========================================================================= */
(function () {
  'use strict';

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------------------------------------------------------------- utils */

  function on(el, evt, fn, opts) {
    if (el) el.addEventListener(evt, fn, opts);
  }

  function focusables(root) {
    return Array.prototype.slice
      .call(root.querySelectorAll('a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])'))
      .filter(function (n) { return n.offsetParent !== null; });
  }

  /* ------------------------------------------------------- reveal on scroll
     Deliberately not IntersectionObserver: during a fast scroll IO coalesces
     deliveries and reports isIntersecting=false for elements that flew past
     between frames, leaving them stuck at opacity 0. A rAF-throttled rect
     check cannot miss, and a timeout backstops it. */
  (function reveal() {
    var pending = new Set(Array.prototype.slice.call(document.querySelectorAll('[data-reveal]')));
    if (!pending.size || reduceMotion) {
      pending.forEach(function (el) { el.classList.add('is-visible'); });
      return;
    }

    var ticking = false;

    function check() {
      ticking = false;
      var limit = window.innerHeight * 0.92;
      pending.forEach(function (el) {
        if (el.getBoundingClientRect().top >= limit) return;
        pending.delete(el);
        var delay = Number(el.dataset.revealDelay || 0);
        if (delay) setTimeout(function () { el.classList.add('is-visible'); }, delay);
        else el.classList.add('is-visible');
      });
      if (!pending.size) teardown();
    }

    function onScroll() {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(check);
    }

    function teardown() {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    check();
    on(window, 'load', function () { setTimeout(check, 400); });
    setTimeout(function () {
      pending.forEach(function (el) { el.classList.add('is-visible'); });
      pending.clear();
    }, 6000);
  })();

  /* ------------------------------------------------------------- header */
  (function header() {
    var el = document.querySelector('[data-header]');
    if (!el) return;

    var threshold = el.classList.contains('is-overlay') ? 160 : 90;
    var ticking = false;

    function onScroll() {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(function () {
        el.classList.toggle('is-stuck', window.scrollY > threshold);
        ticking = false;
      });
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  })();

  /* ------------------------------------------------------ mobile drawer */
  (function drawer() {
    var nav = document.getElementById('mobile-nav');
    var backdrop = document.querySelector('.nav-backdrop');
    var openBtn = document.querySelector('[data-menu-open]');
    if (!nav || !backdrop) return;

    function open() {
      nav.hidden = false;
      backdrop.hidden = false;
      document.body.classList.add('nav-open');
      document.body.style.overflow = 'hidden';
      if (openBtn) openBtn.setAttribute('aria-expanded', 'true');
      var first = focusables(nav)[0];
      if (first) first.focus();
    }

    function close() {
      nav.hidden = true;
      backdrop.hidden = true;
      document.body.classList.remove('nav-open');
      document.body.style.overflow = '';
      if (openBtn) {
        openBtn.setAttribute('aria-expanded', 'false');
        openBtn.focus();
      }
    }

    on(openBtn, 'click', open);
    document.querySelectorAll('[data-menu-close]').forEach(function (n) { on(n, 'click', close); });

    on(document, 'keydown', function (e) {
      if (e.key === 'Escape' && !nav.hidden) { close(); return; }
      if (e.key !== 'Tab' || nav.hidden) return;
      var items = focusables(nav);
      if (!items.length) return;
      var first = items[0];
      var last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    });
  })();

  /* ------------------------------------------------------- search panel */
  (function search() {
    var panel = document.querySelector('[data-search-panel]');
    var openBtn = document.querySelector('[data-search-open]');
    var closeBtn = document.querySelector('[data-search-close]');
    if (!panel || !openBtn) return;

    on(openBtn, 'click', function () {
      var isOpen = !panel.hidden;
      panel.hidden = isOpen;
      openBtn.setAttribute('aria-expanded', String(!isOpen));
      if (!isOpen) {
        var input = panel.querySelector('input');
        if (input) input.focus();
      }
    });

    on(closeBtn, 'click', function () {
      panel.hidden = true;
      openBtn.setAttribute('aria-expanded', 'false');
      openBtn.focus();
    });

    on(document, 'keydown', function (e) {
      if (e.key === 'Escape' && !panel.hidden) {
        panel.hidden = true;
        openBtn.setAttribute('aria-expanded', 'false');
      }
    });
  })();

  /* --------------------------------------------------------- back to top */
  (function backToTop() {
    var btn = document.querySelector('[data-to-top]');
    if (!btn) return;
    function onScroll() { btn.classList.toggle('is-visible', window.scrollY > 700); }
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    on(btn, 'click', function (e) {
      e.preventDefault();
      window.scrollTo({ top: 0, behavior: reduceMotion ? 'auto' : 'smooth' });
    });
  })();

  /* --------------------------------------------------- hero background video
     Autoplay is declared in the markup so the video starts without waiting on
     JS. Here we only handle the two cases markup cannot: a visitor who asked
     for reduced motion, and a tab that is not visible. */
  (function heroVideo() {
    var videos = Array.prototype.slice.call(document.querySelectorAll('.hero-slide video'));
    if (!videos.length) return;

    if (reduceMotion) {
      videos.forEach(function (v) {
        v.removeAttribute('autoplay');
        v.pause();
        // the poster frame stays on screen, so the slide is never blank
        v.setAttribute('preload', 'none');
      });
      return;
    }

    function sync() {
      videos.forEach(function (v) {
        var slide = v.closest('.hero-slide');
        var active = slide && slide.classList.contains('is-active');
        if (document.hidden || !active) {
          v.pause();
        } else {
          var p = v.play();
          // Safari rejects when it decides the gesture rules were not met
          if (p && p.catch) p.catch(function () {});
        }
      });
    }

    on(document, 'visibilitychange', sync);
    document.addEventListener('lafam:heroslide', sync);
    sync();
  })();

  /* ------------------------------------------------------- hero carousel */
  (function hero() {
    var root = document.querySelector('[data-hero]');
    if (!root) return;

    var slides = Array.prototype.slice.call(root.querySelectorAll('[data-slide]'));
    var dots = Array.prototype.slice.call(root.querySelectorAll('[data-hero-dot]'));
    if (slides.length < 2) return;

    var index = 0;
    var timer;
    var duration = Number(root.dataset.interval || 6500);

    function show(next) {
      index = (next + slides.length) % slides.length;
      slides.forEach(function (s, i) {
        s.classList.toggle('is-active', i === index);
        s.setAttribute('aria-hidden', String(i !== index));
      });
      dots.forEach(function (d, i) {
        d.classList.toggle('is-active', i === index);
        d.setAttribute('aria-selected', String(i === index));
      });
      document.dispatchEvent(new CustomEvent('lafam:heroslide'));
    }

    function start() {
      if (reduceMotion) return;
      stop();
      timer = setInterval(function () { show(index + 1); }, duration);
    }
    function stop() { if (timer) clearInterval(timer); }

    on(root.querySelector('[data-hero-next]'), 'click', function () { show(index + 1); start(); });
    on(root.querySelector('[data-hero-prev]'), 'click', function () { show(index - 1); start(); });
    dots.forEach(function (d, i) { on(d, 'click', function () { show(i); start(); }); });

    on(root, 'mouseenter', stop);
    on(root, 'mouseleave', start);
    on(root, 'focusin', stop);
    on(document, 'visibilitychange', function () { document.hidden ? stop() : start(); });

    var x0 = 0;
    on(root, 'touchstart', function (e) { x0 = e.touches[0].clientX; stop(); }, { passive: true });
    on(root, 'touchend', function (e) {
      var dx = e.changedTouches[0].clientX - x0;
      if (Math.abs(dx) > 45) show(index + (dx < 0 ? 1 : -1));
      start();
    }, { passive: true });

    on(root.querySelector('.hero-dots'), 'keydown', function (e) {
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
      e.preventDefault();
      var rtl = document.documentElement.dir === 'rtl';
      var forward = rtl ? e.key === 'ArrowLeft' : e.key === 'ArrowRight';
      show(index + (forward ? 1 : -1));
      dots[index].focus();
      start();
    });

    start();
  })();

  /* -------------------------------------------------- testimonial slider */
  (function quotes() {
    var root = document.querySelector('[data-quotes]');
    if (!root) return;

    var items = Array.prototype.slice.call(root.querySelectorAll('[data-quote]'));
    var dots = Array.prototype.slice.call(root.querySelectorAll('[data-quote-dot]'));
    if (items.length < 2) return;

    var i = 0;
    var timer;

    function show(n) {
      i = (n + items.length) % items.length;
      items.forEach(function (q, k) {
        q.classList.toggle('is-active', k === i);
        q.setAttribute('aria-hidden', String(k !== i));
      });
      dots.forEach(function (d, k) {
        d.classList.toggle('is-active', k === i);
        d.setAttribute('aria-selected', String(k === i));
      });
    }

    function start() { if (!reduceMotion) { stop(); timer = setInterval(function () { show(i + 1); }, 5200); } }
    function stop() { if (timer) clearInterval(timer); }

    dots.forEach(function (d, k) { on(d, 'click', function () { show(k); start(); }); });
    on(root, 'mouseenter', stop);
    on(root, 'mouseleave', start);
    start();
  })();

  /* ------------------------------------------- "the secret" image swapper */
  (function secret() {
    var root = document.querySelector('[data-secret]');
    if (!root) return;

    var lines = Array.prototype.slice.call(root.querySelectorAll('[data-secret-line]'));
    var imgs = Array.prototype.slice.call(root.querySelectorAll('[data-secret-img]'));
    if (!lines.length) return;

    var current = 0;

    function show(n) {
      current = (n + lines.length) % lines.length;
      lines.forEach(function (l, k) { l.classList.toggle('is-active', k === current); });
      imgs.forEach(function (im, k) { im.classList.toggle('is-active', k === current); });
    }

    if (window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
      lines.forEach(function (l, k) { on(l, 'mouseenter', function () { show(k); }); });
    } else if (!reduceMotion) {
      var timer;
      var io = new IntersectionObserver(function (entries) {
        if (entries[0].isIntersecting) timer = setInterval(function () { show(current + 1); }, 2800);
        else if (timer) clearInterval(timer);
      }, { threshold: 0.25 });
      io.observe(root);
    }
  })();

  /* ------------------------------------------------------ product gallery */
  (function gallery() {
    var root = document.querySelector('[data-gallery]');
    if (!root) return;

    var imgs = Array.prototype.slice.call(root.querySelectorAll('[data-gallery-img]'));
    var thumbs = Array.prototype.slice.call(root.querySelectorAll('[data-gallery-thumb]'));
    var current = 0;

    function show(i) {
      current = i;
      imgs.forEach(function (im, n) { im.classList.toggle('is-active', n === i); });
      thumbs.forEach(function (t, n) {
        t.classList.toggle('is-active', n === i);
        t.setAttribute('aria-selected', String(n === i));
      });
    }

    thumbs.forEach(function (t, i) { on(t, 'click', function () { show(i); }); });

    var dlg = document.querySelector('[data-zoom]');
    var zoomImg = document.querySelector('[data-zoom-img]');
    on(document.querySelector('[data-zoom-open]'), 'click', function () {
      if (!dlg || !zoomImg) return;
      var active = imgs[current];
      zoomImg.src = active.currentSrc || active.src;
      zoomImg.alt = active.alt;
      dlg.showModal();
    });
    on(document.querySelector('[data-zoom-close]'), 'click', function () { if (dlg) dlg.close(); });
    on(dlg, 'click', function (e) { if (e.target === dlg) dlg.close(); });
  })();

  /* ==================================================================== */
  /*  Cart — Shopify Cart AJAX API                                        */
  /* ==================================================================== */

  var Cart = {
    /** Re-render server-rendered cart sections instead of formatting money in JS. */
    sectionsToRender: function () {
      return Array.prototype.slice
        .call(document.querySelectorAll('[data-cart-section]'))
        .map(function (el) { return el.dataset.cartSection; });
    },

    request: function (url, body) {
      return fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      }).then(function (res) {
        return res.json().then(function (data) {
          if (!res.ok) throw new Error(data.description || data.message || 'Cart error');
          return data;
        });
      });
    },

    add: function (id, quantity) {
      var sections = this.sectionsToRender();
      return this.request(window.Shopify.routes.root + 'cart/add.js', {
        items: [{ id: id, quantity: quantity }],
        sections: sections.length ? sections : undefined,
      });
    },

    change: function (line, quantity) {
      var sections = this.sectionsToRender();
      return this.request(window.Shopify.routes.root + 'cart/change.js', {
        line: line,
        quantity: quantity,
        sections: sections.length ? sections : undefined,
      });
    },

    /** Paint every badge from the authoritative count the API returned. */
    paintCount: function (count) {
      document.querySelectorAll('[data-cart-count]').forEach(function (el) {
        el.textContent = String(count);
        el.hidden = count === 0;
      });
    },

    /** Swap in freshly rendered markup for any section that asked for it. */
    renderSections: function (data) {
      if (!data || !data.sections) return;
      document.querySelectorAll('[data-cart-section]').forEach(function (el) {
        var html = data.sections[el.dataset.cartSection];
        if (!html) return;
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var fresh = doc.querySelector('[data-cart-section="' + el.dataset.cartSection + '"]');
        if (fresh) el.innerHTML = fresh.innerHTML;
      });
    },

    refreshCount: function () {
      var self = this;
      return fetch(window.Shopify.routes.root + 'cart.js')
        .then(function (r) { return r.json(); })
        .then(function (cart) { self.paintCount(cart.item_count); return cart; });
    },
  };

  window.LafamCart = Cart;

  /* --------------------------------------------------------- add to cart */
  (function addToCart() {
    document.querySelectorAll('[data-atc]').forEach(function (root) {
      var form = root.closest('form');
      var input = root.querySelector('[data-qty-input]');
      var btn = root.querySelector('[data-atc-btn]');
      var label = root.querySelector('[data-atc-label]');
      var msg = root.querySelector('[data-atc-msg]');
      if (!btn || !label) return;

      function clamp(n) { return Math.min(99, Math.max(1, isFinite(n) ? n : 1)); }

      on(root.querySelector('[data-qty-dec]'), 'click', function () {
        if (input) input.value = String(clamp(Number(input.value) - 1));
      });
      on(root.querySelector('[data-qty-inc]'), 'click', function () {
        if (input) input.value = String(clamp(Number(input.value) + 1));
      });
      on(input, 'change', function () { input.value = String(clamp(Number(input.value))); });

      var original = label.textContent;
      var resetTimer;

      function submit(e) {
        if (e) e.preventDefault();
        var variantId = root.dataset.variantId;
        if (!variantId) return;

        btn.disabled = true;
        label.textContent = root.dataset.labelAdding || original;

        Cart.add(variantId, clamp(Number(input ? input.value : 1)))
          .then(function (data) {
            /* Buy now: skip the cart entirely and hand off to checkout. The
               never-settling promise keeps the button disabled and the label on
               "redirecting" until the browser actually leaves the page — without
               it the reset timer repaints the original label mid-navigation. */
            if (root.hasAttribute('data-buy-now')) {
              label.textContent = root.dataset.labelRedirecting || original;
              window.location.href = window.Shopify.routes.root + 'checkout';
              return new Promise(function () {});
            }
            Cart.renderSections(data);
            return Cart.refreshCount();
          })
          .then(function () {
            label.textContent = root.dataset.labelAdded || original;
            btn.classList.add('is-done');
            if (msg) {
              msg.textContent = '';
              var link = document.createElement('a');
              link.href = window.Shopify.routes.root + 'cart';
              link.textContent = root.dataset.labelViewCart || 'Cart';
              link.style.textDecoration = 'underline';
              msg.appendChild(link);
            }
          })
          .catch(function (err) {
            if (msg) msg.textContent = err.message;
            label.textContent = original;
          })
          .then(function () {
            btn.disabled = false;
            clearTimeout(resetTimer);
            resetTimer = setTimeout(function () {
              label.textContent = original;
              btn.classList.remove('is-done');
            }, 2600);
          });
      }

      /* The form posts to /cart/add without JS, so the button keeps working. */
      if (form) on(form, 'submit', submit);
      else on(btn, 'click', submit);
    });
  })();

  /* ------------------------------------------------------- cart line edit */
  function bindCartLines() {
    document.querySelectorAll('[data-line-item]').forEach(function (row) {
      var line = Number(row.dataset.lineItem);

      function update(qty) {
        row.classList.add('is-busy');
        Cart.change(line, qty)
          .then(function (data) {
            Cart.renderSections(data);
            Cart.paintCount(data.item_count);
            bindCartLines();
            var live = document.querySelector('[data-cart-live]');
            if (live) live.textContent = row.dataset.lineTitle || '';
          })
          .catch(function () { row.classList.remove('is-busy'); });
      }

      var qtyInput = row.querySelector('[data-qty]');
      on(row.querySelector('[data-dec]'), 'click', function () { update(Number(qtyInput.value) - 1); });
      on(row.querySelector('[data-inc]'), 'click', function () { update(Number(qtyInput.value) + 1); });
      on(row.querySelector('[data-remove]'), 'click', function (e) { e.preventDefault(); update(0); });
      on(qtyInput, 'change', function () { update(Number(qtyInput.value)); });
    });
  }
  bindCartLines();
  document.addEventListener('shopify:section:load', bindCartLines);

  /* --------------------------------------------- legal page: print + TOC */
  (function legal() {
    on(document.querySelector('[data-print]'), 'click', function () {
      document.querySelectorAll('details').forEach(function (d) { d.open = true; });
      window.print();
    });

    function openTarget() {
      var id = location.hash.slice(1);
      if (!id) return;
      var target = document.getElementById(id);
      if (!target || target.tagName !== 'DETAILS') return;
      target.open = true;
      target.scrollIntoView({ block: 'start', behavior: reduceMotion ? 'auto' : 'smooth' });
    }

    on(window, 'hashchange', openTarget);
    document.querySelectorAll('.legal-toc a, .post-toc a').forEach(function (a) {
      on(a, 'click', function () { requestAnimationFrame(openTarget); });
    });
    openTarget();
  })();

  /* -------------------------------------------------------- copy to clipboard */
  (function copyLink() {
    var btn = document.querySelector('[data-copy]');
    if (!btn) return;
    var original = btn.textContent;
    on(btn, 'click', function () {
      var url = btn.dataset.copy;
      var done = function () {
        btn.textContent = btn.dataset.copied || 'Copied';
        setTimeout(function () { btn.textContent = original; }, 2500);
      };
      if (navigator.clipboard) navigator.clipboard.writeText(url).then(done, done);
      else done();
    });
  })();
})();
